import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { Icon, type IconName } from './Icon';

/* Visual kit for the Ops dashboards (styles/dash.css): figures that count, a sparkline you can
   scrub, a ring, a dot meter, a donut, a side sheet and an anchored popover. No chart library. */

/** No motion: reduced-motion users, and anywhere without a real browser (server render, jsdom). */
const reduced = () => typeof window === 'undefined' || typeof window.matchMedia !== 'function' || typeof requestAnimationFrame !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 4);

/** Tween a number toward `target` (600 ms ease-out); jumps straight there under reduced motion. */
export function useTween(target: number, ms = 700): number {
  const [v, setV] = useState(() => (reduced() ? target : 0));
  const from = useRef(v);
  const raf = useRef(0);
  useEffect(() => {
    if (!Number.isFinite(target)) return;
    if (reduced()) {
      setV(target);
      return;
    }
    const start = performance.now();
    const a = from.current;
    cancelAnimationFrame(raf.current);
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const x = a + (target - a) * easeOut(t);
      from.current = x;
      setV(x);
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, ms]);
  return v;
}

const group = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const FIG_RE = /^([−-]?)(₱?)(\d[\d,]*)(\.\d+)?(.*)$/u;

/**
 * A formatted figure ("₱16,188.60", "90%", "0 / 1", "—") set in the dashboard number face: the
 * currency sign and decimals at half size, the digits counting up when the value changes. The
 * plain text rides along for screen readers (and text search); the styled digits are CSS content.
 */
export function Fig({ text, size = 'md', animate = true, className, style }: { text: string; size?: 'xl' | 'lg' | 'md' | 'sm' | 'xs'; animate?: boolean; className?: string; style?: CSSProperties }) {
  const cls = `fig ${size}${className ? ` ${className}` : ''}`;
  const pair = text.split(' / ');
  if (pair.length === 2) {
    return (
      <span className={cls} style={style}>
        <span className="sr-only">{text}</span>
        <FigPart text={pair[0] ?? ''} animate={animate} />
        <span className="sep" aria-hidden="true" data-t="/" />
        <FigPart text={pair[1] ?? ''} animate={animate} />
      </span>
    );
  }
  return (
    <span className={cls} style={style}>
      <span className="sr-only">{text}</span>
      <FigPart text={text} animate={animate} />
    </span>
  );
}

function FigPart({ text, animate }: { text: string; animate: boolean }) {
  const m = FIG_RE.exec(text.trim());
  const decimals = m?.[4] ? m[4].length - 1 : 0;
  const target = m ? Number((m[3] ?? '0').replace(/,/g, '') + (m[4] ?? '')) : NaN;
  const v = useTween(Number.isFinite(target) ? target : 0);
  // The digits are drawn with CSS generated content (data-t), so the DOM's only text is the plain
  // figure in Fig's sr-only span: screen readers and text search see "₱1,000" once.
  if (!m) return <span aria-hidden="true" data-t={text} />;
  const shown = animate ? v : target;
  const whole = Math.floor(shown + 1e-9);
  const frac = decimals ? (shown - whole).toFixed(decimals).slice(1) : '';
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'baseline' }}>
      {m[1] ? <span data-t={m[1]} /> : null}
      {m[2] ? <span className="cur" data-t={m[2]} /> : null}
      <span data-t={group(whole)} />
      {decimals ? <span className="dec" data-t={frac} /> : null}
      {m[5] ? <span className="suf" data-t={m[5].trim()} /> : null}
    </span>
  );
}

/** Area sparkline you can scrub with the pointer (or arrow keys); the tip shows label + value. */
export function Spark({ values, labels, format, ariaLabel }: { values: number[]; labels: string[]; format(v: number): string; ariaLabel: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [len, setLen] = useState(2000);
  const n = values.length;
  const max = Math.max(1, ...values);
  const H = 100;
  const pts = values.map((v, i) => [n === 1 ? 50 : (i / (n - 1)) * 100, H - 8 - (Math.max(0, v) / max) * (H - 20)] as const);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  useLayoutEffect(() => {
    // The stroke is non-scaling, so dashes are in screen pixels: the box's width plus its height
    // bounds the drawn length.
    const r = box.current?.getBoundingClientRect();
    if (r) setLen(Math.ceil(r.width + r.height * 2));
  }, [d]);
  if (!n) return null;
  const pick = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    const t = (e.clientX - r.left) / r.width;
    setHover(Math.max(0, Math.min(n - 1, Math.round(t * (n - 1)))));
  };
  const h = hover === null ? null : pts[hover];
  return (
    <div
      ref={box}
      className="dv-spark"
      role="img"
      aria-label={ariaLabel}
      tabIndex={0}
      onPointerMove={pick}
      onPointerDown={pick}
      onPointerLeave={() => setHover(null)}
      onBlur={() => setHover(null)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') setHover((x) => Math.min(n - 1, (x ?? -1) + 1));
        else if (e.key === 'ArrowLeft') setHover((x) => Math.max(0, (x ?? n) - 1));
        else if (e.key === 'Escape') setHover(null);
      }}
    >
      <svg viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <line className="base" x1="0" x2="100" y1={H - 8} y2={H - 8} />
        <path className="area" d={`${d} L100,${H - 8} L0,${H - 8} Z`} />
        <path className="line" d={d} style={{ ['--len' as string]: len }} />
      </svg>
      {h ? (
        <>
          <span className="rule" style={{ left: `${h[0]}%` }} />
          <span className="dot" style={{ left: `${h[0]}%`, top: `${h[1]}%` }} />
          <span className="dv-tip" style={{ left: `clamp(70px, ${h[0]}%, calc(100% - 70px))`, top: `${h[1]}%` }}>
            <small>{labels[hover ?? 0]}</small>
            <Fig text={format(values[hover ?? 0] ?? 0)} size="sm" animate={false} />
          </span>
        </>
      ) : null}
    </div>
  );
}

/** Ring gauge for a percentage; tone picks the colour. */
export function Ring({ pct, tone, children }: { pct: number | null; tone: 'good' | 'warn' | 'bad'; children?: ReactNode }) {
  const r = 32;
  const c = 2 * Math.PI * r;
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') {
      setShown(pct ?? 0);
      return;
    }
    const t = requestAnimationFrame(() => setShown(pct ?? 0));
    return () => cancelAnimationFrame(t);
  }, [pct]);
  return (
    <span style={{ position: 'relative', display: 'inline-grid', placeItems: 'center' }} aria-hidden="true">
      <svg className="dv-ring" viewBox="0 0 76 76">
        <circle className="trk" cx="38" cy="38" r={r} />
        {pct !== null ? <circle className={`val ${tone}`} cx="38" cy="38" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(100, Math.max(0, shown)) / 100)} /> : null}
      </svg>
      {children ? <span style={{ position: 'absolute' }}>{children}</span> : null}
    </span>
  );
}

/** One pill per rider: filled when online, Cart Blue when on a delivery. */
export function DotMeter({ total, on, busy, cap = 18 }: { total: number; on: number; busy: number; cap?: number }) {
  const shown = Math.min(total, cap);
  return (
    <span className={total <= 8 ? 'dv-meter few' : 'dv-meter'} aria-hidden="true">
      {Array.from({ length: shown }, (_, i) => (
        <i key={i} className={i < busy ? 'busy' : i < on ? 'on' : ''} />
      ))}
      {total > cap ? <span className="more">+{total - cap}</span> : null}
    </span>
  );
}

export interface Slice {
  key: string;
  label: string;
  value: number;
  swatch: string;
}

/** Donut + legend; hovering either side highlights the slice and the centre shows its value. */
export function Donut({ slices, total, format, centerLabel }: { slices: Slice[]; total: number; format(v: number): string; centerLabel: string }) {
  const [hi, setHi] = useState<string | null>(null);
  const r = 70;
  const c = 2 * Math.PI * r;
  const sum = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  const gap = sum && slices.filter((s) => s.value > 0).length > 1 ? 4 : 0;
  let acc = 0;
  const cur = slices.find((s) => s.key === hi);
  return (
    <div className="dv-mix">
      <div className="dv-donut" role="img" aria-label={`${centerLabel} ${format(total)}: ${slices.map((s) => `${s.label} ${format(s.value)}`).join(', ')}`}>
        <svg viewBox="0 0 168 168" aria-hidden="true">
          <circle className="trk" cx="84" cy="84" r={r} />
          {sum
            ? slices.map((s) => {
                const len = (Math.max(0, s.value) / sum) * c;
                const off = acc;
                acc += len;
                if (len <= 0) return null;
                return (
                  <circle
                    key={s.key}
                    className={hi ? (hi === s.key ? 'hi' : 'dim') : ''}
                    cx="84"
                    cy="84"
                    r={r}
                    style={{ stroke: `var(--pc-category-${s.swatch === 'other' ? 'creators' : s.swatch === 'express' ? 'express' : s.swatch})` }}
                    strokeDasharray={`${Math.max(0, len - gap)} ${c}`}
                    strokeDashoffset={-off}
                    onPointerEnter={() => setHi(s.key)}
                    onPointerLeave={() => setHi(null)}
                  />
                );
              })
            : null}
        </svg>
        <div className="mid">
          <span className="dv-sub">{cur ? cur.label : centerLabel}</span>
          <Fig text={format(cur ? cur.value : total)} size="md" animate={!cur} />
        </div>
      </div>
      <div className="dv-legend">
        {slices.map((s) => (
          <button key={s.key} type="button" className={hi === s.key ? 'hi' : ''} onPointerEnter={() => setHi(s.key)} onPointerLeave={() => setHi(null)} onFocus={() => setHi(s.key)} onBlur={() => setHi(null)}>
            <span className={`sw sw-${s.swatch}`} />
            <span className="nm">
              {s.label}
              <span className="pc">{sum ? Math.round((Math.max(0, s.value) / sum) * 100) : 0}%</span>
            </span>
            <Fig text={format(s.value)} size="xs" animate={false} />
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Side sheet on <dialog>: slides in from the right, Esc / backdrop / close button glide it out.
 * The dialog gives the focus trap and the inert page.
 */
export function Sheet({ open, title, onClose, children, actions }: { open: boolean; title: string; onClose(): void; children: ReactNode; actions?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.classList.remove('closing');
      d.showModal();
    } else if (!open && d.open) {
      if (reduced()) {
        d.close();
        setMounted(false);
        return;
      }
      d.classList.add('closing');
      const t = window.setTimeout(() => {
        d.classList.remove('closing');
        d.close();
        setMounted(false);
      }, 200);
      return () => window.clearTimeout(t);
    }
  }, [open, mounted]);
  if (!mounted) return null;
  return (
    <dialog
      ref={ref}
      className="dv-sheet"
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dv-sheet-h">
        <h2 id={id}>{title}</h2>
        {actions}
        <button type="button" className="dv-ibtn press" onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>
      </div>
      <div className="dv-sheet-b">{children}</div>
    </dialog>
  );
}

/** Anchored popover: a pressable trigger and a panel that closes on outside click or Esc. */
export function Popover({ trigger, label, children }: { trigger(p: { open: boolean; toggle(): void; id: string }): ReactNode; label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        wrap.current?.querySelector<HTMLElement>('button')?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className="dv-pop-wrap" ref={wrap}>
      {trigger({ open, toggle: () => setOpen((o) => !o), id })}
      {open ? (
        <div
          className="dv-pop"
          id={id}
          role="dialog"
          aria-label={label}
          onClick={(e) => {
            // Picking a menu item closes the menu (RowMenu and any menu built on Popover).
            if ((e.target as HTMLElement).closest('[role="menuitem"]')) setOpen(false);
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export interface RowAction {
  label: string;
  icon?: IconName;
  tone?: 'danger';
  disabled?: boolean;
  onSelect(): void;
}

/**
 * A row's secondary actions behind one "⋯" pressable (keeps tables about the data). Put the one
 * main action as a visible button next to it when a row has one.
 */
export function RowMenu({ label, items }: { label: string; items: RowAction[] }) {
  return (
    <Popover
      label={label}
      trigger={({ open, toggle, id }) => (
        <button type="button" className="dv-kebab press" aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle}>
          <span aria-hidden="true">•••</span>
        </button>
      )}
    >
      <div className="dv-menu" role="menu">
        {items.map((it) => (
          <button key={it.label} type="button" role="menuitem" className={it.tone === 'danger' ? 'danger' : ''} disabled={it.disabled} onClick={it.onSelect}>
            {it.icon ? <Icon name={it.icon} /> : null}
            {it.label}
          </button>
        ))}
      </div>
    </Popover>
  );
}

/** Page header: title, an optional live/meta line, actions on the right. */
export function PageHead({ title, meta, live, actions }: { title: string; meta?: ReactNode; live?: boolean; actions?: ReactNode }) {
  return (
    <header className="dv-head">
      <div className="stack s4">
        <h1>{title}</h1>
        {meta || live ? (
          <span className="dv-live">
            {live ? <span className="dv-pulse" aria-hidden="true" /> : null}
            {meta}
          </span>
        ) : null}
      </div>
      {actions ? <div className="dv-actions">{actions}</div> : null}
    </header>
  );
}

/** A row of stat tiles (label + big figure + optional sub line and visual), clickable when onOpen. */
export function Stat({ label, value, sub, visual, onOpen, tone }: { label: string; value: string; sub?: ReactNode; visual?: ReactNode; onOpen?(): void; tone?: 'ink' | 'alert' }) {
  return (
    <section className={`card dv-stat${onOpen ? ' dv-tile' : ''}${tone ? ` dv-${tone}` : ''}`} onClick={onOpen} aria-label={label}>
      <div className="dv-tile-top">
        <span className="dv-label">{label}</span>
        {onOpen ? (
          <button
            type="button"
            className="dv-open press"
            aria-label={`Open ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            <Icon name="chevronRight" />
          </button>
        ) : null}
      </div>
      <div className="dv-tile-body">
        <div className="stack s4" style={{ minWidth: 0 }}>
          <Fig text={value} size="lg" />
          {sub ? <span className="dv-sub">{sub}</span> : null}
        </div>
        {visual}
      </div>
    </section>
  );
}
